import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import TextField from "@mui/material/TextField";
import { styled } from "@mui/material/styles";
import { MDBBtn, MDBCol, MDBRow } from "mdb-react-ui-kit";
import ReactDatePicker from "react-datepicker";
import { ClapSpinner } from "react-spinners-kit";
import Confetti from "react-confetti";
import axios from "axios";
import { hostName } from "../../../../App";
import { useNavigate } from "react-router-dom";
import RegisterPledger from "../register/RegisterPledger";

// custom text field
const CustomTextField = styled(TextField)`
  & label.Mui-focused {
    color: #007c16;
  }
  & .MuiOutlinedInput-root {
    &.Mui-focused fieldset {
      border: 1px solid;
      border-color: #007c16;
    }
  }
  &:hover {
    & label {
      color: #007c16;
    }
    & .MuiOutlinedInput-root {
      fieldset {
        border-color: #007c16;
      }
    }
  }
`;

// backshadow variants
const backVariants = {
  hidden: {
    opacity: 0,
  },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.5,
    },
  },
};

// modal variants
const modalVariants = {
  hidden: {
    scale: 0,
  },
  visible: {
    scale: 1,
    transition: {
      duration: 0.5,
    },
  },
};

const PledgeModal = ({
  setPledgeModal,
  pledgeObj,
  orgObj,
  orgName,
  pledgeAmount,
  handlePledgeAmount,
  userData,
  setLoggedInUserData,
}) => {
  // translation
  const { t } = useTranslation();

  // step -- pledge | success OR register
  const [step, setStep] = useState("pledge");

  // box reference
  const box = useRef(null);

  // email
  const [email, setEmail] = useState(userData?.email || "");

  // update email with userData.email if available
  useEffect(() => {
    if (userData) {
      setEmail(userData.email);
    } else {
      setEmail("");
    }
  }, [userData]);

  // Function to get default installments based on frequency
  const getDefaultInstallments = (frequency) => {
    switch (frequency) {
      case "ONETIME":
        return 1;
      case "DAILY":
        return 30;
      case "WEEKLY":
        return 52;
      case "BIWEEKLY":
        return 26;
      case "MONTHLY":
        return 12;
      default:
        return null;
    }
  };

  // State for frequency
  const [frequency, setFrequency] = useState(
    pledgeObj?.frequency?.includes("ONETIME")
      ? "ONETIME"
      : pledgeObj?.frequency?.[0]
  );

  // Update state when `pledgeObj` changes
  useEffect(() => {
    if (pledgeObj?.frequency?.length > 0) {
      const newFrequency = pledgeObj.frequency.includes("ONETIME")
        ? "ONETIME"
        : pledgeObj.frequency[0];
      setFrequency(newFrequency);
      setInstallments(getDefaultInstallments(newFrequency));
    }
  }, [pledgeObj?.frequency]);

  // State for installments, initialized based on frequency
  const [installments, setInstallments] = useState(
    getDefaultInstallments(frequency)
  );

  // calculate due date
  const calculateDueDate = (startDate, frequency, installments) => {
    if (!startDate || !installments || !frequency) return null;
    if (frequency === "ONETIME") return startDate;

    const start = new Date(startDate);
    let dueDate = new Date(start);

    switch (frequency) {
      case "DAILY":
        dueDate.setDate(start.getDate() + parseFloat(installments));
        break;
      case "WEEKLY":
        dueDate.setDate(start.getDate() + parseFloat(installments) * 7);
        break;
      case "BIWEEKLY":
        dueDate.setDate(start.getDate() + parseFloat(installments) * 14);
        break;
      case "MONTHLY":
        dueDate.setMonth(start.getMonth() + parseFloat(installments));
        break;
      default:
        return startDate;
    }

    return dueDate;
  };

  // format date
  const formatDate = (dateObj) => {
    if (dateObj) {
      const year = dateObj.getFullYear().toString();
      const month = (dateObj.getMonth() + 1).toString().padStart(2, "0");
      const day = dateObj.getDate().toString().padStart(2, "0");
      return `${year}-${month}-${day}`;
    }
  };

  // start date
  const [startDate, setStartDate] = useState(new Date());

  // due date
  const [dueDate, setDueDate] = useState(() =>
    calculateDueDate(startDate, frequency, installments)
  );

  // update due date when frequency, installments or start date changes
  useEffect(() => {
    setDueDate(calculateDueDate(startDate, frequency, installments));
  }, [startDate, frequency, installments]);

  // handle frequency buttons
  const handleFrequency = (text) => {
    setFrequency(text);
    setInstallments(getDefaultInstallments(text));
  };

  // handle input box event for no of installments
  const handleInstallmentsValue = (value) => {
    if (value) {
      const intValue = value.replace(/\D/g, ""); // Remove non-digits
      setInstallments(intValue.replace(/^0+/, "")); // Also remove leading zeros
    } else {
      setInstallments("");
    }
  };

  // notes
  const [notes, setNotes] = useState("");

  // hide name
  const [hideName, setHideName] = useState("N");

  // hide name change
  const handleHideNameChange = () => {
    setHideName((prevStatus) => (prevStatus === "Y" ? "N" : "Y"));
  };

  // transaction states
  const [loader, setLoader] = useState(false);
  const [successMsg, setSuccessMsg] = useState(null);
  const [code, setCode] = useState(null);
  const [successObj, setSuccessObj] = useState(null);
  const [pledgeError, setPledgeError] = useState(null);
  // option states
  const [countries, setCountries] = useState([]);
  const [usaStates, setUsaStates] = useState([]);
  const [provinces, setProvinces] = useState([]);

  // handle pledge transaction
  const handlePledgeTransaction = (e) => {
    e.preventDefault();

    if (
      !loader &&
      pledgeAmount &&
      parseFloat(pledgeAmount) >= parseFloat(pledgeObj.minimumAmount) &&
      installments &&
      (frequency === "ONETIME" || parseFloat(installments) >= 2)
    ) {
      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      if (emailRegex.test(email)) {
        setLoader(true);
        setPledgeError(null);
        setSuccessMsg(null);
        setCode(null);
        setSuccessObj(null);

        // Check if email exists in database
        axios
          .get(`${hostName}api/v1/auth/exist?email=${email}`)
          .then((res) => {
            if (res.data.exist) {
              axios
                .post(
                  `${hostName}api/v1/pledge/enroll`,
                  {
                    headerTag: pledgeObj.tagNumber,
                    amount: parseFloat(pledgeAmount),
                    email,
                    frequency: frequency.charAt(0),
                    installments,
                    startDate: formatDate(startDate),
                    dueDate: formatDate(dueDate),
                    notes,
                    anonymous: hideName,
                  },
                  {
                    headers: {
                      "X-Local-Access-Token":
                        "zrPIG1Q90I0cfPhmBZuH1hQErA1mUvQG7mE4jk3ta8lqBJw1L15TGvqJD5MMxwAO",
                    },
                  }
                )
                .then((res) => {
                  setStep("success");
                  setCode(res.data.data.code);
                  setSuccessMsg(res.data.messageDetails);
                  setSuccessObj(res.data.data);
                })
                .catch((err) => {
                  const data = err?.response?.data;

                  const hasErrors =
                    Array.isArray(data?.errors) && data.errors.length > 0;
                  const hasMessageDetails =
                    Array.isArray(data?.messageDetails) &&
                    data.messageDetails.length > 0;

                  setPledgeError(
                    (hasErrors && data.errors) ||
                      (hasMessageDetails && data.messageDetails) ||
                      data?.message ||
                      err?.message
                  );
                })
                .finally(() => setLoader(false));
            } else {
              // Handle registration options
              Promise.all([
                axios.get(`${hostName}api/v1/public/country`),
                axios.get(`${hostName}api/v1/public/state?countryCode=US`),
                axios.get(`${hostName}api/v1/public/state?countryCode=ca`),
              ])
                .then(([countriesRes, usaStatesRes, provincesRes]) => {
                  const countryOptions = countriesRes.data.map((country) => ({
                    value: country.code,
                    label: country.name,
                  }));
                  setCountries(countryOptions);

                  const usaStatesOptions = usaStatesRes.data.map((state) => ({
                    value: state.code,
                    label: state.name,
                  }));
                  setUsaStates(usaStatesOptions);

                  const provincesOptions = provincesRes.data.map(
                    (province) => ({
                      value: province.code,
                      label: province.name,
                    })
                  );
                  setProvinces(provincesOptions);

                  setStep("register");
                })
                .catch((error) => {
                  setPledgeError(
                    `${error.message} in fetching countries block of code.`
                  );
                })
                .finally(() => setLoader(false));
            }
          })
          .catch((err) => {
            setLoader(false);
            setPledgeError(
              `${err.message} in verifying email existence. Please try again later.`
            );
          });
      } else {
        setPledgeError("Email field should be a valid email address.");
      }
    }
  };

  // confetti dimensions
  const [confettiWidth, setConfettiWidth] = useState(0);
  const [confettiHeight, setConfettiHeight] = useState(0);

  // is confetti
  const [isConfetti, setIsConfetti] = useState(false);

  // update is confetti
  useEffect(() => {
    if (step === "success" && code && successMsg && successObj) {
      setIsConfetti(true);

      setTimeout(() => {
        setIsConfetti(false);
      }, 7000);
    } else {
      setIsConfetti(false);
    }
  }, [step, code, successMsg, successObj]);

  // update dimensions
  useEffect(() => {
    if (box && box.current) {
      setConfettiWidth(box.current.offsetWidth);
      setConfettiHeight(box.current.offsetHeight);
    }
  }, [box]);

  // navigate
  const navigate = useNavigate();

  return (
    <AnimatePresence>
      <motion.div
        className={`backdrop${step === "register" ? " registeration" : ""}`}
        variants={backVariants}
        initial="hidden"
        animate="visible"
        ref={box}
      >
        {/* show confetti here */}
        {step === "success" &&
          code &&
          successMsg &&
          successObj &&
          isConfetti && (
            <Confetti
              width={confettiWidth}
              height={confettiHeight}
              numberOfPieces={2000}
              recycle={false}
            />
          )}

        <motion.div
          className={`${
            step === "register"
              ? "registeration-modal"
              : "donation-modal pledge"
          }`}
          variants={modalVariants}
          initial="hidden"
          animate="visible"
        >
          {/* step is not register here */}
          {step !== "register" ? (
            // donate box
            <div className="donate-box">
              {/* loader */}
              {loader && (
                <div className="loading-screen">
                  <ClapSpinner size={30} color="#007c16" loading={loader} />
                </div>
              )}

              {/* cross button */}
              <button
                type="button"
                className="cross-icon donations"
                onClick={() => setPledgeModal(false)}
                disabled={loader}
                style={{ fontSize: "24px" }}
              >
                &times;
              </button>

              {/* header */}
              <header className="modal-header">
                <h3>{pledgeObj.name}</h3>
                <div style={{ color: "#007c16" }}>{pledgeObj.campaign}</div>
                <div>{orgObj.name}</div>
              </header>

              {/* pledge details */}
              {step === "pledge" && (
                // form
                <form onSubmit={handlePledgeTransaction}>
                  {/* amount */}
                  <MDBRow className="mb-3">
                    <MDBCol className="col-12 col-md-12 mb-2 mb-md-0">
                      <div
                        className="donation-text"
                        style={{ textTransform: "uppercase" }}
                      >
                        {t("pledge.pledged_amount")}
                        <span
                          className={`minimum-amount-msg${
                            pledgeAmount !== "" &&
                            parseFloat(pledgeAmount) <
                              parseFloat(pledgeObj.minimumAmount)
                              ? " red"
                              : ""
                          }`}
                          style={{
                            marginLeft: "10px",
                            textTransform: "capitalize",
                          }}
                        >
                          {t("DonationModal.minimum_amount")}{" "}
                          {pledgeObj.currencySymbol}
                          {pledgeObj.minimumAmount}
                        </span>
                      </div>
                      <CustomTextField
                        variant="outlined"
                        fullWidth
                        autoComplete="off"
                        size="small"
                        value={pledgeAmount}
                        onChange={(e) => {
                          let newValue = e.target.value;
                          if (isNaN(newValue) || parseFloat(newValue) < 0) {
                            newValue = "0";
                          }
                          handlePledgeAmount(newValue);
                        }}
                        onKeyDown={(e) => {
                          const parts = e.target.value.split(".");

                          // Allow numeric keys, Backspace, Delete, navigation keys, and decimal point
                          if (
                            !/[0-9]/.test(e.key) &&
                            ![
                              "Backspace",
                              "Enter",
                              "Delete",
                              "ArrowLeft",
                              "ArrowRight",
                              "ArrowUp",
                              "ArrowDown",
                              ".",
                            ].includes(e.key)
                          ) {
                            e.preventDefault();
                          }
                          // Prevent typing more than two decimal places
                          else if (
                            e.key !== "Backspace" &&
                            parts.length > 1 &&
                            parts[1].length >= 2 &&
                            /[0-9]/.test(e.key)
                          ) {
                            e.preventDefault();
                          }
                          // Prevent typing second decimal point
                          else if (
                            e.key === "." &&
                            e.target.value.includes(".")
                          ) {
                            e.preventDefault();
                          }
                        }}
                        placeholder={t("pledge.pledged_amount")}
                        helperText={
                          pledgeAmount !== "" &&
                          parseFloat(pledgeAmount) <
                            parseFloat(pledgeObj.minimumAmount)
                            ? `${t("CampaignDetails.amount_error_msg")} ${
                                pledgeObj.minimumAmount
                              }`
                            : ""
                        }
                        error={
                          pledgeAmount !== "" &&
                          parseFloat(pledgeAmount) <
                            parseFloat(pledgeObj.minimumAmount)
                        }
                      />
                    </MDBCol>
                  </MDBRow>

                  {/* email */}
                  <MDBRow className="mb-3">
                    <MDBCol className="col-12 col-md-12 mb-2 mb-md-0">
                      <div
                        className="donation-text"
                        style={{
                          textTransform: "uppercase",
                        }}
                      >
                        {t("DonationModal.email")}
                      </div>
                      <input
                        type="email"
                        className="edit-media-inputs"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        placeholder={t("DonationModal.email")}
                        readOnly={userData}
                      />
                    </MDBCol>
                  </MDBRow>

                  {/* frequency */}
                  <MDBRow className="mb-3">
                    <MDBCol className="col-12 col-md-12 mb-2 mb-md-0">
                      <div
                        className="donation-text"
                        style={{
                          textTransform: "uppercase",
                          fontSize: "13px",
                        }}
                      >
                        {t("pledge.how_often")}
                      </div>

                      <div className="often-btns-group">
                        {pledgeObj?.frequency?.length > 0 &&
                          pledgeObj.frequency.map((freq, index) => (
                            <button
                              key={index}
                              type="button"
                              id={freq}
                              className={frequency === freq ? "active" : ""}
                              onClick={(e) =>
                                handleFrequency(e.currentTarget.id)
                              }
                            >
                              {t(`DonationModal.${freq.toLowerCase()}`)}
                            </button>
                          ))}
                      </div>
                    </MDBCol>
                  </MDBRow>

                  {/* if frequency is NOT ONETIME */}
                  {frequency !== "ONETIME" && (
                    <MDBRow className="mb-3">
                      <MDBCol className="col-12 col-md-12 mb-2 mb-md-0">
                        <div className="how-often-div">
                          {/* # of installments */}
                          <div
                            className="donations-text"
                            style={{ textTransform: "uppercase" }}
                          >
                            # {t("pledge.of_installments").toUpperCase()}
                          </div>

                          <CustomTextField
                            variant="outlined"
                            fullWidth
                            size="small"
                            required
                            value={installments}
                            onChange={(e) => {
                              let newValue = e.target.value;
                              if (isNaN(newValue) || parseInt(newValue) < 0) {
                                newValue = "0";
                              }
                              handleInstallmentsValue(newValue);
                            }}
                            onKeyDown={(e) => {
                              // Only allow numeric keys, backspace, delete, navigation
                              if (
                                !/[0-9]/.test(e.key) &&
                                ![
                                  "Backspace",
                                  "Enter",
                                  "Delete",
                                  "ArrowLeft",
                                  "ArrowRight",
                                  "ArrowUp",
                                  "ArrowDown",
                                  "Tab",
                                ].includes(e.key)
                              ) {
                                e.preventDefault();
                              }
                            }}
                            placeholder={t("pledge.installments")}
                            helperText={
                              installments !== "" && parseInt(installments) < 2
                                ? `${t("pledge.installments_error_msg")} ${2}`
                                : ""
                            }
                            error={
                              installments !== "" && parseInt(installments) < 2
                            }
                          />
                        </div>
                      </MDBCol>
                    </MDBRow>
                  )}

                  {/* start date */}
                  <MDBRow className="mb-3">
                    <MDBCol className="col-12 col-md-12 mb-2 mb-md-0">
                      <label
                        htmlFor="startDate"
                        style={{
                          fontSize: "14px",
                          fontWeight: "600",
                          textTransform: "uppercase",
                        }}
                      >
                        {t("DonationModal.start_date")}{" "}
                        {frequency !== "ONETIME" &&
                          ` - ${t(`DonationModal.${frequency.toLowerCase()}`)}`}
                      </label>
                      <ReactDatePicker
                        style={{
                          width: "100%",
                        }}
                        className="edit-media-inputs"
                        selected={startDate}
                        onChange={(date) => setStartDate(date)}
                        dateFormat="yyyy-MM-dd"
                        placeholderText={t("DonationModal.start_date")}
                        required
                        minDate={new Date()}
                      />
                    </MDBCol>
                  </MDBRow>

                  {/* due date */}
                  <MDBRow className="mb-3">
                    <MDBCol className="col-12 col-md-12 mb-2 mb-md-0">
                      <label
                        htmlFor="dueDate"
                        style={{
                          fontSize: "14px",
                          fontWeight: "600",
                          textTransform: "uppercase",
                        }}
                      >
                        {t("pledge.due_date")}
                      </label>
                      <ReactDatePicker
                        style={{
                          width: "100%",
                        }}
                        className="edit-media-inputs"
                        selected={dueDate}
                        onChange={(date) => setDueDate(date)}
                        dateFormat="yyyy-MM-dd"
                        placeholderText={t("pledge.due_date")}
                        required
                        readOnly
                        minDate={new Date()}
                      />
                    </MDBCol>
                  </MDBRow>

                  {/* hide name from supporters list */}
                  <MDBRow className="mb-3">
                    <MDBCol className="col-12 col-md-12 mb-2 mb-md-0">
                      <h6
                        htmlFor="notes"
                        style={{
                          fontSize: "14px",
                          fontWeight: "600",
                          textTransform: "uppercase",
                        }}
                      >
                        {t("pledge.hide_name")}
                      </h6>
                      <label className="switch">
                        <input
                          type="checkbox"
                          checked={hideName === "Y"}
                          onChange={handleHideNameChange}
                        />
                        <span className="slider round"></span>
                      </label>
                    </MDBCol>
                  </MDBRow>

                  {/* notes */}
                  <MDBRow className="mb-3">
                    <MDBCol className="col-12 col-md-12 mb-2 mb-md-0">
                      <label
                        htmlFor="notes"
                        style={{
                          fontSize: "14px",
                          fontWeight: "600",
                          textTransform: "uppercase",
                        }}
                      >
                        {t("pledge.notes")}
                      </label>
                      <textarea
                        className="edit-media-inputs text-area"
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                      />
                    </MDBCol>
                  </MDBRow>

                  {pledgeError && (
                    <div className="error-msg" style={{ width: "100%" }}>
                      {Array.isArray(pledgeError) ? (
                        // If transactionError is an array
                        pledgeError.map((msg, index) => (
                          <p key={index} style={{ marginBottom: 0 }}>
                            {msg}
                          </p>
                        ))
                      ) : typeof transactionError === "object" ? (
                        // If transactionError is an object
                        Object.keys(pledgeError).map((field, index) => (
                          <p key={index} style={{ marginBottom: 0 }}>
                            {Array.isArray(pledgeError[field])
                              ? pledgeError[field][0]
                              : pledgeError[field]}
                          </p>
                        ))
                      ) : (
                        // If transactionError is a simple string
                        <>{pledgeError}</>
                      )}
                    </div>
                  )}

                  <div className="back-and-next-div">
                    <MDBBtn
                      type="button"
                      className="back-step-btn"
                      onClick={() => {
                        setPledgeModal(false);
                      }}
                      disabled={loader}
                    >
                      {t("DonationModal.close")}
                    </MDBBtn>
                    <MDBBtn
                      type="submit"
                      className="next-btn"
                      disabled={
                        loader ||
                        !pledgeAmount ||
                        parseFloat(pledgeAmount) <
                          parseFloat(pledgeObj.minimumAmount) ||
                        !installments ||
                        (frequency !== "ONETIME" &&
                          parseFloat(installments) < 2)
                      }
                    >
                      {t("pledge.pledge")}
                    </MDBBtn>
                  </div>
                </form>
              )}

              {/* success screen */}
              {step === "success" && successMsg && code && successObj && (
                <div className="success-container">
                  {/* success msg */}
                  <div className="pledge-success-msg">{successMsg}</div>

                  {/* pledge tag */}
                  <h6 className="pledge-tag">{t("pledge.pledge_tag")}</h6>

                  {/* Tag Number */}
                  <div className="tag-number">{successObj.pledgeTag}</div>

                  {/* info msg */}
                  <div className="info-msg">
                    {t("pledge.would_you_like_to_setup_payment")}
                  </div>

                  {/* yes and no btns */}
                  <div className="button-container">
                    <MDBBtn
                      type="button"
                      className="back-step-btn"
                      onClick={() => {
                        setPledgeModal(false);
                      }}
                      style={{ marginRight: "8px" }}
                    >
                      {t("pledge.no")}
                    </MDBBtn>
                    <MDBBtn
                      type="button"
                      className="next-btn"
                      onClick={() =>
                        navigate(`/sites/${orgName}/fulfillpledge/${code}`)
                      }
                    >
                      {t("pledge.yes")}
                    </MDBBtn>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <RegisterPledger
              setLoggedInUserData={setLoggedInUserData}
              setStep={setStep}
              pledgeObj={pledgeObj}
              donationAmount={pledgeAmount}
              frequency={frequency}
              installments={installments}
              donorEmail={email}
              startDate={startDate}
              dueDate={dueDate}
              formatDate={formatDate}
              notes={notes}
              loader={loader}
              setLoader={setLoader}
              setSuccessMsg={setSuccessMsg}
              setSuccessObj={setSuccessObj}
              setCode={setCode}
              setPledgeError={setPledgeError}
              countries={countries}
              usaStates={usaStates}
              provinces={provinces}
            />
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default PledgeModal;
